// frontend/src/contexts/SessionActivityContext.tsx
import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { getIdleTimeoutMs, WARNING_LEAD_MS } from '../services/sessionConfig';
import { useSessionGuard } from './SessionGuardContext';
import { gracefulExit, setSessionEndSink } from '../session/graceful-exit';
import { refreshAccessToken } from '../services/authApi';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;
const ACTIVITY_THROTTLE_MS = 30 * 1000;
const TICK_MS = 1000;

type Api = { warningOpen: boolean; secondsLeft: number; extend: () => void };
const Ctx = createContext<Api | null>(null);

export function SessionActivityProvider(
  { children, enabled = true, timeoutMsOverride }:
  { children: React.ReactNode; enabled?: boolean; timeoutMsOverride?: number },
) {
  const { getForms } = useSessionGuard();
  const [idleMs, setIdleMs] = useState<number>(timeoutMsOverride ?? 120 * 60 * 1000);
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const lastActivity = useRef<number>(Date.now());
  const lastThrottle = useRef<number>(0);
  const exiting = useRef(false);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stable refs so the interval effect never needs to re-run on state changes.
  const warningOpenRef = useRef(false);
  warningOpenRef.current = warningOpen;

  // Load the configured timeout once (unless a test override is given).
  useEffect(() => {
    if (timeoutMsOverride != null || !enabled) return;
    let alive = true;
    getIdleTimeoutMs().then(ms => { if (alive) setIdleMs(ms); });
    return () => { alive = false; };
  }, [enabled, timeoutMsOverride]);

  const doExit = useCallback(() => {
    if (exiting.current) return;
    exiting.current = true;
    setWarningOpen(false);
    void gracefulExit(getForms());
  }, [getForms]);

  // Keep a stable ref so the interval callback always calls the latest version
  // without adding doExit to the interval effect's dependency array.
  const doExitRef = useRef(doExit);
  doExitRef.current = doExit;

  const extend = useCallback(() => {
    lastActivity.current = Date.now();
    setWarningOpen(false);
    refreshAccessToken().catch(() => { /* lazy interceptor will catch a real failure */ });
  }, []);

  // Register the axios sink so a hard 401/refresh-failure also exits gracefully.
  useEffect(() => { setSessionEndSink(doExit); }, [doExit]);

  // Activity listener — uses warningOpenRef so this callback is stable (no warningOpen dep).
  const onActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottle.current < ACTIVITY_THROTTLE_MS) return;
    lastThrottle.current = now;
    lastActivity.current = now;
    if (warningOpenRef.current) setWarningOpen(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled) return;
    ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, onActivity, { passive: true }));
    // Only reset lastActivity when the effect mounts/idleMs changes — not on warningOpen changes.
    lastActivity.current = Date.now();
    tick.current = setInterval(() => {
      const remaining = idleMs - (Date.now() - lastActivity.current);
      if (remaining <= 0) { doExitRef.current(); return; }
      if (remaining <= WARNING_LEAD_MS) {
        setWarningOpen(true);
        setSecondsLeft(Math.ceil(remaining / 1000));
      } else if (warningOpenRef.current) {
        setWarningOpen(false);
      }
    }, TICK_MS);
    return () => {
      ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, onActivity));
      if (tick.current) clearInterval(tick.current);
    };
  }, [enabled, idleMs, onActivity]); // doExit + warningOpen intentionally via refs

  return <Ctx.Provider value={{ warningOpen, secondsLeft, extend }}>{children}</Ctx.Provider>;
}

export function useSessionActivity(): Api {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSessionActivity must be used within SessionActivityProvider');
  return ctx;
}
